export const AuthorBadge = () => {
	return (
		<div className="flex items-center justify-center rounded-lg border border-sand-500 px-1 py-0.5 text-xs text-sand-500">
			<p>Author</p>
		</div>
	);
};

export const MeBadge = () => {
	return (
		<div className="flex items-center justify-center rounded-lg border border-clearSky-500 px-1 py-0.5 text-xs text-clearSky-500">
			<p>Me</p>
		</div>
	);
};

export const FeedbackBadge = ({
	decision,
}: {
	decision: "FEEDBACK_GIVEN" | "APPROVED" | "REJECTED" | "EXPIRED" | "IN_REVIEW" | "CONDITIONALLY_APPROVED";
}) => {
	switch (decision) {
		case "FEEDBACK_GIVEN":
			return (
				<div className="flex w-fit items-center justify-center whitespace-nowrap rounded-full bg-nightSky px-2 py-1 font-semibold text-sand-50">
					<p className="!text-xs">FEEDBACK GIVEN</p>
				</div>
			);
		case "APPROVED":
			return (
				<div className="flex w-fit items-center justify-center rounded-full bg-grass-500 px-2 py-1 font-semibold">
					<p className="!text-xs">APPROVED</p>
				</div>
			);
		case "CONDITIONALLY_APPROVED":
			return (
				<div className="flex w-fit items-center justify-center rounded-full bg-grass-500 px-2 py-1 font-semibold">
					<p className="!text-xs">CONDITIONALLY APPROVED</p>
				</div>
			);
		case "REJECTED":
			return (
				<div className="flex w-fit items-center justify-center whitespace-nowrap rounded-full bg-red-500 px-2 py-1 font-semibold">
					<p className="!text-xs">NOT APPROVED</p>
				</div>
			);
		case "EXPIRED":
			return (
				<div className="flex w-fit items-center justify-center rounded-full bg-nightSky px-2 py-1 font-semibold text-sand-50">
					<p className="!text-xs">EXPIRED</p>
				</div>
			);
		case "IN_REVIEW":
			return (
				<div className="flex w-fit items-center justify-center whitespace-nowrap rounded-full  bg-sand-600 px-2 py-1 font-semibold text-sand">
					<p className="!text-xs">PENDING</p>
				</div>
			);
		default:
			return <></>;
	}
};

export const ApprovedBadge = () => {
	return (
		<div className="flex w-20 items-center justify-center rounded-full bg-grass-500 px-1 py-0.5 text-xs font-semibold">
			<p>APPROVED</p>
		</div>
	);
};

export const NotApprovedBadge = () => {
	return (
		<div className="flex w-28 items-center justify-center rounded-full bg-red-500 px-1 py-0.5 text-xs font-semibold">
			<p>NOT APPROVED</p>
		</div>
	);
};

export const NewFeatureBadge = () => {
	return (
		<div className="relative h-0 w-0">
			<p className="h-45  absolute right-[-8.5rem] top-[-2.7rem] z-10 flex w-10 justify-center bg-grass-500 p-1 pl-3 pr-2 text-xs">
				NEW!
			</p>
		</div>
	);
};

export const RedDot = () => {
	return (
		<div className="relative ">
			<div className="absolute right-[-0.7rem] top-[-1.3rem] h-2 w-2 rounded-full bg-danger"></div>
		</div>
	);
};

export const UnreadNotificationDot = () => {
	return <span className="mr-[1px] inline-block h-2 w-2 rounded-full bg-grass-500"></span>;
};

interface StatusColorMap {
	[key: string]: string;
}

const statusColorMap: StatusColorMap = {
	DRAFT: "bg-baseCloud",
	SUBMITTED: "bg-sand-300",
	IN_REVIEW: "bg-sand-300",
	ACTIVE: "bg-grass",
	COMPLETED: "bg-baseCloud",
	CONDITIONALLY_APPROVED: "bg-sand-300",
	PUBLIC_COMMENTARY: "bg-sand-300",
	PUBLIC_COMMENTARY_COMPLETE: "bg-sand-300",
	FINAL_VOTING: "bg-sand-300",
	APPROVED: "bg-grass-500",
};

export const customStatusLabels: Record<string, string> = {
	["CONDITIONALLY_APPROVED"]: "Ready for Public Commentary",
	["PUBLIC_COMMENTAR_EXPIRED"]: "Public Commentary Expired",
	["FINAL_VOTING"]: "v1.0 Candidate",
};

export const upgradeStatusLabels: Record<string, string> = {
	["DRAFT"]: "DRAFT",
	["SUBMITTED"]: "SUBMITTED",
	["APPROVED"]: "APPROVED",
	["PUBLIC_COMMENTARY"]: "UPDATE IN REVIEW",
	["PUBLIC_COMMENTARY_COMPLETE"]: "UPDATE IN REVIEW",
	["FINAL_VOTING"]: "UPDATE IN REVIEW",
};

const methodologyStatusColorMap: StatusColorMap = {
	...statusColorMap,
};

const upgradeStatusColorMap: StatusColorMap = {
	DRAFT: "bg-baseCloud",
	SUBMITTED: "bg-clearSky-300",
	PUBLIC_COMMENTARY: "bg-sand-300",
	PUBLIC_COMMENTARY_COMPLETE: "bg-sand-300",
	FINAL_VOTING: "bg-sand-300",
};

type MethodologyUpgradeCandidate = {
	upgradeType?: string;
	upgradeStatus?: string;
	reviewPhase?: string;
};

export const GenericStatusBadge = ({ capCaseStatusString }: { capCaseStatusString: string }) => {
	if (!capCaseStatusString) {
		return null;
	}
	const bgColor = statusColorMap[capCaseStatusString] || statusColorMap.DEFAULT;
	return (
		<div>
			<span className={`rounded-full ${bgColor} px-2.5 py-0.5 !font-geist-mono text-xs font-medium`}>
				{customStatusLabels[capCaseStatusString] ?? capCaseStatusString.split("_").join(" ")}
			</span>
		</div>
	);
};

export const MethodologyStatusBadge = ({
	status,
	upgradeCandidate,
}: {
	status?: string;
	upgradeCandidate?: MethodologyUpgradeCandidate | null;
}) => {
	const isUpgrade = Boolean(upgradeCandidate);
	const resolvedStatus = isUpgrade ? upgradeCandidate?.reviewPhase || upgradeCandidate?.upgradeStatus : status;

	if (!resolvedStatus) {
		return null;
	}

	const bgColor = isUpgrade ? upgradeStatusColorMap[resolvedStatus] : methodologyStatusColorMap[resolvedStatus];
	const label = isUpgrade
		? upgradeStatusLabels[resolvedStatus] ?? resolvedStatus.split("_").join(" ")
		: customStatusLabels[resolvedStatus] ?? resolvedStatus.split("_").join(" ");

	return (
		<div>
			<span
				className={`rounded-full ${bgColor} px-2.5 py-0.5 !font-geist-mono text-xs font-medium ${
					isUpgrade ? "uppercase" : ""
				}`}
			>
				{label}
			</span>
		</div>
	);
};

const projectStatusColorMap: StatusColorMap = {
	VALIDATED: "bg-grass",
	IN_VALIDATION: "bg-baseCloud",
	ACTIVE: "bg-clearSky-300",
	COMPLETED: "bg-grass",
};

export const GenericProjectStatusBadge = ({
	capCaseStatusString,
	endDate,
}: {
	capCaseStatusString: string;
	endDate?: string;
}) => {
	if (!capCaseStatusString) {
		return null;
	}

	let label = capCaseStatusString.split("_").join(" ");
	let statusForColor = capCaseStatusString;

	if (capCaseStatusString === "VALIDATED" && endDate) {
		const currentDate = new Date();
		const projectEndDate = new Date(endDate);

		if (currentDate > projectEndDate) {
			label = "COMPLETED";
			statusForColor = "COMPLETED";
		} else {
			label = "ACTIVE";
			statusForColor = "ACTIVE";
		}
	}

	const bgColor = projectStatusColorMap[statusForColor] || projectStatusColorMap.DRAFT;
	return (
		<div>
			<span className={`rounded-full ${bgColor} px-2.5 py-0.5 !font-geist-mono text-xs font-medium`}>{label}</span>
		</div>
	);
};

export const ReviwerMarkedReadyBadge = ({
	reviewerName,
	isFinalRound = false,
}: {
	reviewerName: string;
	isFinalRound?: boolean;
}) => {
	if (!reviewerName) {
		return null;
	}
	return (
		<div>
			<span
				className={`!w-fit rounded-full border border-black bg-grass px-3 py-1 text-xs font-medium ${
					isFinalRound ? "bg-gray-300" : ""
				}`}
			>
				{reviewerName}
			</span>
		</div>
	);
};

export const AuthorBadgeV2 = () => (
	<span className="ml-1 h-fit rounded-full border border-sand-600 px-2 py-[1px] text-xs font-medium text-sand-600">
		Author
	</span>
);

export const MeBadgeV2 = () => (
	<span className="ml-1 h-fit rounded-full border border-sand-600 px-2 py-[1px] !font-geist-mono text-xs font-medium text-sand-600">
		Me
	</span>
);

type ReviewerStatus =
	| "IN_REVIEW"
	| "MARKED"
	| "FEEDBACK_GIVEN"
	| "EXPIRED"
	| "IN_ACTIVE"
	| "APPROVED"
	| "REJECTED"
	| "ABSTAIN";

type BadgeConfig = {
	label: string;
	bgColor: string;
};

const getReviewerStatusBadgeConfig = (status: ReviewerStatus | undefined): BadgeConfig | null => {
	if (!status) return null;

	switch (status) {
		case "IN_REVIEW":
			return { label: "Pending", bgColor: "bg-transparent" };
		case "MARKED":
			return { label: "Marked as Ready", bgColor: "bg-grass-500" };
		case "FEEDBACK_GIVEN":
			return { label: "Feedback given", bgColor: "bg-sand-300" };
		case "EXPIRED":
			return { label: "Expired", bgColor: "bg-red-300" };
		case "IN_ACTIVE":
			return { label: "Inactive", bgColor: "bg-red-500" };
		case "APPROVED":
			return { label: "Approved", bgColor: "bg-grass-500" };
		case "REJECTED":
			return { label: "Rejected", bgColor: "bg-red-400" };
		case "ABSTAIN":
			return { label: "Abstained", bgColor: "bg-gray-300" };
		default:
			return null;
	}
};

export const ReviewerDecisionBadgeV2 = ({ decision }: { decision: ReviewerStatus | undefined }) => {
	const badgeConfig = getReviewerStatusBadgeConfig(decision);

	if (!badgeConfig) {
		return null;
	}

	return (
		<div>
			<span
				className={`!w-fit rounded-full border border-black ${badgeConfig.bgColor} px-2 py-0.5 text-xs font-medium`}
			>
				{badgeConfig.label}
			</span>
		</div>
	);
};

export const UploadedByValidatorBadge = () => {
	return (
		<div className="flex w-fit items-center justify-center rounded-full bg-grass-500 px-2 py-1 text-xs font-semibold">
			<p>Uploaded by Validator</p>
		</div>
	);
};

export const ReviewerTagBadgeV2 = ({ tagLabel, fontSize = "xs" }: { tagLabel: string; fontSize?: "xs" | "sm" }) => {
	if (!tagLabel) {
		return null;
	}
	return (
		<div>
			<span
				className={`!w-fit rounded-full border border-gray-400 px-2 py-[1px] !font-geist-mono text-${fontSize} font-medium text-gray-500`}
			>
				{tagLabel}
			</span>
		</div>
	);
};
